import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from 'recharts';

import type { SocialAnalyticsDay } from '@nucleus-commerce/api-client/admin';
import { moneyToDisplayNumber } from '@nucleus-commerce/core';
import { useFormat } from '@nucleus-commerce/i18n/react';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@nucleus-commerce/ui/components/chart';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useDayFormat } from '../hooks/use-day-format';

export interface ClicksSpendChartProps {
  series: readonly SocialAnalyticsDay[];
  /** The spend line exists only with an ad account connected. */
  adsConnected: boolean;
  currency: string;
  /** Read by screen readers in place of the chart. */
  summary: string;
  className?: string;
}

/** Outbound clicks as bars on the left axis; with ads connected, spend as a line on the right one. */
export function ClicksSpendChart({
  series,
  adsConnected,
  currency,
  summary,
  className,
}: ClicksSpendChartProps) {
  const { t } = useTranslation('social');
  const format = useFormat();
  const days = useDayFormat();

  const config = useMemo(
    () =>
      ({
        clicks: { label: t('analytics.chart.clicks'), color: 'var(--chart-1)' },
        spend: { label: t('analytics.chart.spend'), color: 'var(--chart-2)' },
      }) satisfies ChartConfig,
    [t],
  );

  const data = useMemo(
    () =>
      series.map((day) => ({
        label: days.short(day.day),
        clicks: day.outboundClicks,
        spend: day.adSpend ? moneyToDisplayNumber(day.adSpend) : 0,
      })),
    [series, days],
  );

  const compactMoney = (value: number) =>
    new Intl.NumberFormat(format.locale, {
      style: 'currency',
      currency,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);

  return (
    <figure className={cn('m-0', className)}>
      <figcaption className="sr-only">{summary}</figcaption>
      <ChartContainer config={config} className="aspect-auto h-[280px] w-full" aria-hidden>
        <ComposedChart data={data} margin={{ left: 4, right: 4, top: 8 }}>
          <CartesianGrid vertical={false} />
          <XAxis dataKey="label" tickLine={false} axisLine={false} minTickGap={16} />
          <YAxis
            yAxisId="clicks"
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
            width={40}
          />
          {adsConnected ? (
            <YAxis
              yAxisId="spend"
              orientation="right"
              tickLine={false}
              axisLine={false}
              width={64}
              tickFormatter={compactMoney}
            />
          ) : null}
          <ChartTooltip
            content={
              <ChartTooltipContent
                formatter={(value, name) => (
                  <div className="flex w-full items-center justify-between gap-3">
                    <span className="text-muted-foreground">
                      {name === 'spend' ? t('analytics.chart.spend') : t('analytics.chart.clicks')}
                    </span>
                    <span className="font-mono font-medium tabular-nums">
                      {name === 'spend'
                        ? format.number(Number(value), { style: 'currency', currency })
                        : format.number(Number(value))}
                    </span>
                  </div>
                )}
              />
            }
          />
          <ChartLegend content={<ChartLegendContent />} />
          <Bar yAxisId="clicks" dataKey="clicks" fill="var(--color-clicks)" radius={[3, 3, 0, 0]} />
          {adsConnected ? (
            <Line
              yAxisId="spend"
              dataKey="spend"
              type="monotone"
              stroke="var(--color-spend)"
              strokeWidth={2}
              dot={false}
            />
          ) : null}
        </ComposedChart>
      </ChartContainer>
    </figure>
  );
}
